import React, { useMemo, useState } from "react";
import { Form, Row, Col, Button, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  FaUser,
  FaBuilding,
  FaInfoCircle,
  FaRobot,
  FaGlobe,
  FaShareAlt,
  FaCopy,
  FaCheck,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { Icon } from "../../../../../components/common/Icon";
import { ChatbotFormData } from "../types";
import { AIChatbot } from "../../../../../features/ai_chatbots/aiChatbotsService";

interface GeneralTabProps {
  formData: ChatbotFormData;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
  setFormData: React.Dispatch<React.SetStateAction<ChatbotFormData>>;
  companiesOptions: any[];
  isCompanyOwner: boolean;
  defaultCompanyId?: number;
  chatbot?: AIChatbot;
}

// Mirrors app/utils/slug.py on the backend so the URL preview matches what
// the API's /page/{company_slug}/{widget_key} endpoint resolves to.
const TURKISH_MAP: Record<string, string> = {
  ç: "c", Ç: "c", ğ: "g", Ğ: "g", ı: "i", I: "i", İ: "i",
  ö: "o", Ö: "o", ş: "s", Ş: "s", ü: "u", Ü: "u",
};

const slugify = (text?: string): string => {
  if (!text) return "company";
  const replaced = text.replace(/[çÇğĞıIİöÖşŞüÜ]/g, (ch) => TURKISH_MAP[ch] ?? ch);
  const normalized = replaced.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
  const slug = normalized.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase();
  return slug || "company";
};

const GeneralTab: React.FC<GeneralTabProps> = ({
  formData,
  handleInputChange,
  setFormData,
  companiesOptions,
  isCompanyOwner,
  defaultCompanyId,
  chatbot,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const publicBaseUrl = useMemo(() => {
    return (
      (process.env.REACT_APP_PUBLIC_URL as string | undefined) ||
      (typeof window !== "undefined" ? window.location.origin : "https://meylo.app")
    );
  }, []);

  const selectedCompany = useMemo(
    () =>
      companiesOptions.find(
        (c) => String(c.id) === String(formData.company_id)
      ),
    [companiesOptions, formData.company_id]
  );

  const publicUrl = useMemo(() => {
    const widgetKey = chatbot?.widget_key;
    const companyName = selectedCompany?.company_name;
    if (!widgetKey || !companyName) return "";
    return `${publicBaseUrl}/${slugify(companyName)}/${widgetKey}`;
  }, [publicBaseUrl, chatbot?.widget_key, selectedCompany?.company_name]);

  const handleCopy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      /* noop – clipboard API can be blocked in insecure contexts */
    }
  };

  return (
    <div className="chatbots-edit-tab-content">
      <Form>
        <Row>
          <Col md={6}>
            <Form.Group className="form-group-custom">
              <Form.Label className="form-label-custom">
                <Icon icon={FaUser} className="me-2" />
                {t("chatbot_name")} *
              </Form.Label>
              <Form.Control
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t("enter_chatbot_name")}
                className="form-control-custom"
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="form-group-custom">
              <Form.Label className="form-label-custom">
                <Icon icon={FaBuilding} className="me-2" />
                {t("company")} *
              </Form.Label>
              <Form.Select
                id="company_id"
                name="company_id"
                value={formData.company_id}
                onChange={handleInputChange}
                disabled={isCompanyOwner || !!defaultCompanyId}
                className="form-control-custom"
                required
              >
                <option value="">{t("Select a company")}</option>
                {companiesOptions.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.company_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="form-group-custom">
          <Form.Label className="form-label-custom">
            <Icon icon={FaInfoCircle} className="me-2" />
            {t("description")}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder={t("describe_what_this_chatbot_does")}
            className="form-control-custom"
          />
        </Form.Group>

        <Form.Group className="form-group-custom">
          <Form.Label className="form-label-custom">
            <Icon icon={FaRobot} className="me-2" />
            {t("initial_message")}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            id="initial_message"
            name="initial_message"
            value={formData.initial_message}
            onChange={handleInputChange}
            placeholder={t("first_message_users_will_see")}
            className="form-control-custom"
          />
        </Form.Group>

        <Form.Group className="form-group-custom">
          <Form.Label className="form-label-custom">
            <Icon icon={FaGlobe} className="me-2" />
            {t("allowed_domains")}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            id="allowed_domains"
            name="allowed_domains"
            value={formData.allowed_domains}
            onChange={handleInputChange}
            placeholder={"https://example.com\nhttps://another-domain.com"}
            className="form-control-custom"
          />
          <Form.Text className="form-text-custom">
            <Icon icon={FaInfoCircle} className="me-1" />
            {t("Leave empty to allow all domains. Enter one domain per line.")}
          </Form.Text>
        </Form.Group>

        <Row>
          <Col md={12}>
            <Form.Group className="form-group-custom">
              <Form.Label className="form-label-custom">{t("status")}</Form.Label>
              <div className="custom-switch-container">
                <Form.Check
                  type="switch"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      is_active: e.target.checked,
                    })
                  }
                  label={formData.is_active ? t("active") : t("inactive")}
                />
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <Form.Group className="form-group-custom">
              <Form.Label className="form-label-custom">
                <Icon icon={FaShareAlt} className="me-2" />
                {t("enable_public_page")}
              </Form.Label>
              <div className="custom-switch-container">
                <Form.Check
                  type="switch"
                  id="public_page_enabled"
                  name="public_page_enabled"
                  checked={!!formData.public_page_enabled}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      public_page_enabled: e.target.checked,
                    })
                  }
                  label={
                    formData.public_page_enabled ? t("enabled") : t("disabled")
                  }
                />
              </div>
              <Form.Text className="form-text-custom">
                <Icon icon={FaInfoCircle} className="me-1" />
                {t("public_page_description")}
              </Form.Text>

              {formData.public_page_enabled && (
                <div className="mt-3">
                  <Form.Label className="form-label-custom">
                    {t("public_page_url")}
                  </Form.Label>
                  {publicUrl ? (
                    <InputGroup>
                      <Form.Control
                        type="text"
                        value={publicUrl}
                        readOnly
                        className="form-control-custom"
                        onFocus={(e) => (e.target as HTMLInputElement).select()}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={handleCopy}
                        title={t("copy_link")}
                      >
                        <Icon icon={copied ? FaCheck : FaCopy} className="me-1" />
                        {copied ? t("copied") : t("copy_link")}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        as="a"
                        href={publicUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        title={t("open_link")}
                      >
                        <Icon icon={FaExternalLinkAlt} />
                      </Button>
                    </InputGroup>
                  ) : (
                    <Form.Text className="form-text-custom">
                      <Icon icon={FaInfoCircle} className="me-1" />
                      {t("public_page_url_available_after_save")}
                    </Form.Text>
                  )}
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default GeneralTab;
